import React, { useState } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// DuracaoModal — tela de "Definir Duração" (r207)
// ────────────────────────────────────────────────────────────────────────────
// Nasceu dentro do ApontamentoModal (r189) e virou componente pra ser o mesmo
// nas duas telas que apontam horas: apontamento de tarefa e lançamento de horas.
//
// Acumulador: os botões +1..+8 somam horas; -15/+15/+30/+45 mexem nos minutos.
// O total vira a duração aplicada — no apontamento vira o horário de fim, no
// lançamento vira a quantidade de horas.
//
// Props:
//   - inicioStr:  'YYYY-MM-DDTHH:mm' — usado só pra exibir o "Fim calculado"
//   - minutosIni: duração já existente, pra reabrir a tela com o valor certo
//   - onAplicar(minutos), onFechar
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
};

// ─── Regra dos 15 minutos (r207) ───────────────────────────────────────────
// Todo apontamento — início, fim e duração — anda de 15 em 15 minutos.
export const PASSO_MINUTOS = 15;

// 'HH:mm' → minutos desde a meia-noite (null se vazio/inválido)
export function minutosDeHora(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!isFinite(h) || !isFinite(m)) return null;
  return h * 60 + m;
}

// minutos desde a meia-noite → 'HH:mm'
export function horaDeMinutos(min) {
  const t = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

export function ehMultiploDoPasso(hhmm) {
  const min = minutosDeHora(hhmm);
  return min === null ? true : min % PASSO_MINUTOS === 0;
}

export function duracaoEhMultipla(horasDecimais) {
  const min = Math.round((parseFloat(horasDecimais) || 0) * 60);
  return min % PASSO_MINUTOS === 0;
}

// ─── As duas opções válidas em volta do que foi digitado (r208) ────────────
// Nada é arredondado por conta própria: 08:12 devolve ['08:00', '08:15'] e
// quem escolhe é o usuário. Vazio ou já múltiplo devolve lista vazia.
export function opcoesDeHora(hhmm) {
  const min = minutosDeHora(hhmm);
  if (min === null || min % PASSO_MINUTOS === 0) return [];
  const abaixo = Math.floor(min / PASSO_MINUTOS) * PASSO_MINUTOS;
  const acima  = abaixo + PASSO_MINUTOS;
  const opcoes = [{ valor: horaDeMinutos(abaixo), rotulo: horaDeMinutos(abaixo) }];
  // 24:00 não existe como horário do dia — aí sobra só a opção de baixo.
  if (acima < 1440) opcoes.push({ valor: horaDeMinutos(acima), rotulo: horaDeMinutos(acima) });
  return opcoes;
}

// Idem para DURAÇÃO decimal: 8.2h devolve 8h (08:00) e 8.25h (08:15).
export function opcoesDeDuracao(horasDecimais) {
  const min = Math.round((parseFloat(horasDecimais) || 0) * 60);
  if (min < 0 || min % PASSO_MINUTOS === 0) return [];
  const abaixo = Math.floor(min / PASSO_MINUTOS) * PASSO_MINUTOS;
  const acima  = abaixo + PASSO_MINUTOS;
  const rotulo = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  const opcoes = [{ valor: abaixo / 60, rotulo: rotulo(abaixo) }];
  if (acima <= 24 * 60) opcoes.push({ valor: acima / 60, rotulo: rotulo(acima) });
  return opcoes;
}

// ─── Helpers de data ───────────────────────────────────────────────────────
function partesDeIso(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return { data: '', hora: '' };
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
  return { data: local.slice(0, 10), hora: local.slice(11, 16) };
}

export function somarMinutos(iniStr, minutos) {
  const base = new Date(iniStr);
  if (isNaN(base)) return { data: '', hora: '' };
  return partesDeIso(new Date(base.getTime() + minutos * 60000).toISOString());
}

// ─── Botões do acumulador ──────────────────────────────────────────────────
const HORAS_OPCOES = [1, 2, 3, 4, 5, 6, 7, 8];
// r207: o antigo +5 saiu (quebrava a regra dos 15min) e deu lugar ao -15,
// que subtrai — é o jeito de corrigir quando passa do ponto.
const MINUTOS_OPCOES = [-15, 15, 30, 45];
const TETO_MINUTOS = 24 * 60;

const botaoSoma = {
  height: '38px', cursor: 'pointer',
  border: `1px solid ${COR.azul}`, borderRadius: '5px',
  backgroundColor: COR.azul, color: COR.branco,
  fontWeight: '700', fontSize: '13px',
  fontFamily: "'Manrope', sans-serif",
};

const gradeBotoes = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' };

function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
        fontFamily: "'Manrope', sans-serif",
      }}>{label}</label>
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// AjusteMultiploModal (r208) — o horário/duração digitado não fecha na grade
// de 15 minutos. Em vez de arredondar por conta própria, mostra as duas
// possibilidades e deixa a escolha com quem está lançando.
// ════════════════════════════════════════════════════════════════════════════
export function AjusteMultiploModal({ titulo, valorDigitado, opcoes, onEscolher, onCancelar }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10020,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        width: '400px', maxWidth: '92vw', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 18px', backgroundColor: COR.azul, color: COR.branco,
          fontSize: '14px', fontWeight: '700',
          fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          ⏱ {titulo || 'Horário não permitido'}
        </div>

        <div style={{ padding: '16px 18px', fontSize: '13px', color: COR.texto, lineHeight: 1.5 }}>
          <div>
            <strong style={{ color: COR.azul, fontSize: '16px' }}>{valorDigitado}</strong> não é
            permitido — o apontamento anda de {PASSO_MINUTOS} em {PASSO_MINUTOS} minutos.
          </div>
          <div style={{ marginTop: '10px', marginBottom: '8px', color: COR.muted }}>
            Escolha uma das opções válidas:
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {opcoes.map(o => (
              <button key={o.rotulo} type="button" onClick={() => onEscolher(o.valor)}
                      style={{
                        flex: 1, height: '46px',
                        border: `1px solid ${COR.azul}`, borderRadius: '5px',
                        backgroundColor: COR.azul, color: COR.branco,
                        fontSize: '16px', fontWeight: '700', cursor: 'pointer',
                        fontFamily: "'Manrope', sans-serif",
                      }}>{o.rotulo}</button>
            ))}
          </div>
        </div>

        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button onClick={onCancelar} style={{
            padding: '8px 18px', fontSize: '12px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}>Corrigir na mão</button>
        </div>
      </div>
    </div>
  );
}

// r208: o acumulador começa sempre do zero, mesmo reabrindo um apontamento
// que já tem duração — antes ele vinha com o valor atual e somava em cima.
function DuracaoModal({ inicioStr, onAplicar, onFechar }) {
  const [total, setTotal] = useState(0);

  // Nunca passa de 24h nem fica negativo.
  const somar = (min) => setTotal(t => Math.max(0, Math.min(TETO_MINUTOS, t + min)));

  const previsto = total > 0 && inicioStr ? somarMinutos(inicioStr, total) : null;
  const rotuloTotal = `${Math.floor(total / 60)}h${String(total % 60).padStart(2, '0')}`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10010,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        width: '420px', maxWidth: '92vw', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 18px', backgroundColor: COR.azul, color: COR.branco,
          fontSize: '14px', fontWeight: '700',
          fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          ⏳ Definir duração
        </div>

        <div style={{ padding: '16px 18px' }}>
          <Campo label="Horas">
            <div style={gradeBotoes}>
              {HORAS_OPCOES.map(h => (
                <button key={h} type="button" onClick={() => somar(h * 60)}
                        style={botaoSoma}>+{h}</button>
              ))}
            </div>
          </Campo>

          <Campo label="Minutos">
            <div style={gradeBotoes}>
              {MINUTOS_OPCOES.map(m => {
                const ehSubtracao = m < 0;
                // Sem nada acumulado não há o que subtrair.
                const bloqueado = ehSubtracao && total < Math.abs(m);
                return (
                  <button key={m} type="button" onClick={() => somar(m)}
                          disabled={bloqueado}
                          title={ehSubtracao ? 'Tirar 15 minutos do total' : `Somar ${m} minutos`}
                          style={{
                            ...botaoSoma,
                            cursor: bloqueado ? 'not-allowed' : 'pointer',
                            opacity: bloqueado ? 0.4 : 1,
                          }}>
                    {ehSubtracao ? `−${Math.abs(m)}` : `+${String(m).padStart(2, '0')}`}
                  </button>
                );
              })}
            </div>
          </Campo>

          {/* Total acumulado + limpar */}
          <div style={{
            padding: '10px 12px',
            backgroundColor: total > 0 ? COR.azulBg : COR.cinzaBg,
            border: `1px solid ${total > 0 ? COR.azul + '33' : COR.borda}`,
            borderRadius: '5px', fontSize: '12px',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontWeight: '600', color: COR.muted }}>Total:</span>
            <span style={{
              fontWeight: '700', fontSize: '18px', marginRight: 'auto',
              color: total > 0 ? COR.azul : COR.muted,
            }}>{rotuloTotal}</span>
            <button type="button" onClick={() => setTotal(0)} disabled={total === 0}
                    title="Zerar horas e minutos" style={{
                      padding: '5px 12px', fontSize: '11px', fontWeight: '600',
                      border: `1px solid ${COR.azul}`, borderRadius: '5px',
                      backgroundColor: COR.azul, color: COR.branco,
                      cursor: total === 0 ? 'not-allowed' : 'pointer',
                      opacity: total === 0 ? 0.5 : 1,
                      fontFamily: "'Manrope', sans-serif",
                    }}>🗑 Limpar</button>
          </div>

          {inicioStr && (
            <div style={{
              padding: '8px 12px', marginTop: '8px',
              backgroundColor: previsto ? COR.azulBg : COR.cinzaBg,
              border: `1px solid ${previsto ? COR.azul + '33' : COR.borda}`,
              borderRadius: '5px', fontSize: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontWeight: '600', color: COR.muted }}>Fim calculado:</span>
              <span style={{ fontWeight: '700', color: previsto ? COR.azul : COR.muted }}>
                {previsto
                  ? `${previsto.data.split('-').reverse().join('/')} ${previsto.hora}`
                  : '—'}
              </span>
            </div>
          )}
        </div>

        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
        }}>
          <button onClick={onFechar} style={{
            padding: '8px 18px', fontSize: '12px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}>Cancelar</button>
          <button onClick={() => onAplicar(total)} disabled={total <= 0} style={{
            padding: '8px 22px', fontSize: '12px', fontWeight: '700',
            border: `1px solid ${COR.azul}`, borderRadius: '5px',
            backgroundColor: COR.azul, color: COR.branco,
            cursor: total <= 0 ? 'not-allowed' : 'pointer',
            opacity: total <= 0 ? 0.6 : 1,
            fontFamily: "'Manrope', sans-serif",
          }}>✓ Aplicar</button>
        </div>
      </div>
    </div>
  );
}

export default DuracaoModal;
